import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

/**
 * Hands a path off to another application on this origin.
 *
 * `/tools` is served by the separate kx-tools app, not by this one. The sidebar
 * is built from the backend's menu data, which still lists tools, and its
 * entries are ordinary routerLinks — so clicking one asks *this* router to match
 * a route that no longer exists here and lands on 404.
 *
 * The navigation has to be a real page load. Only then does the Ingress (or, in
 * development, the /tools entry in proxy.conf.json) see the request and route it
 * to the other app; a client-side navigation never leaves the browser.
 *
 * Crossing between the two apps is a page load rather than an in-app transition.
 * That is the accepted cost of keeping tools out of this application.
 */
@Injectable({ providedIn: 'root' })
export class ExternalAppGuard implements CanActivate {

  constructor(private router: Router) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    // Already standing on the URL we are about to load: the hand-off did not
    // happen — the serving layer fell back to this app's index.html instead of
    // the other app (Ingress misrouted, or kx-tools not deployed / not running
    // on the port proxy.conf.json points at). Reassigning location here would
    // reload straight back into this guard, forever, so stop and show 404.
    if (window.location.pathname === state.url.split(/[?#]/)[0]) {
      return this.router.parseUrl('/404');
    }

    window.location.href = state.url;
    return false;
  }
}
